import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button, Spin, Modal, message, Typography
} from "antd";
import {
  PlayCircleOutlined, ClockCircleOutlined, BookOutlined,
  TrophyOutlined, CheckCircleOutlined, StarOutlined,
  SafetyCertificateOutlined, ThunderboltOutlined,
  HeartOutlined, ShareAltOutlined, AppstoreOutlined,
  TeamOutlined, FileTextOutlined, ArrowRightOutlined,
  LockOutlined, GlobalOutlined, CustomerServiceOutlined,
  ArrowLeftOutlined, ShoppingCartOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";

import publicCourseService from "../../services/publicCourseService";
import API_BASE from "../../config/apiConfig";
import authHeader from "../../services/auth-header";
import PaymentWrapper from "../payment/PaymentWrapper";

const API_BASE_URL = `${API_BASE}/images/proxy`;

/* ============ Couleurs Nomidya ============ */
const C = {
  blue: "#0054A6",
  blueDark: "#003B75",
  blueDeep: "#001d3d",
  orange: "#E8860C",
  orangeLight: "#FF8C00",
  green: "#00875A",
  text: "#2d3748",
  textLight: "#718096",
  bg: "#f7fafc",
  border: "#e2e8f0",
};

/* ============ Helpers ============ */
const hasContent = (val) => val && val.trim && val.trim().length > 0 && val.trim() !== "<p></p>";
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};
const splitItems = (html) => {
  if (!html) return [];
  const liMatches = html.match(/<li[^>]*>(.*?)<\/li>/gi);
  if (liMatches && liMatches.length > 0) {
    return liMatches.map(li => stripHtml(li).trim()).filter(t => t.length > 0);
  }
  return html.split(/<br\s*\/?>|<\/p>|<p>|\n/).map(s => stripHtml(s).trim()).filter(t => t.length > 0);
};
const getUserId = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user.id || null;
};

/* ================================================================
   CourseLandingPage — Page de vente avec paiement
   ================================================================ */
const CourseLandingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [imageUrl, setImageUrl] = useState("/default-image.png");
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  // Payment states
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const data = await publicCourseService.getCourseById(id);
        setCourse(data);
        if (data?.image) {
          setImageUrl(`${API_BASE_URL}?url=${encodeURIComponent(data.image)}`);
        }
      } catch (err) {
        console.error("Erreur chargement cours:", err);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchCourse();
  }, [id]);

  /* ---- Payment handlers ---- */
  const handleBuyCourse = () => {
    const userId = getUserId();
    if (!userId) {
      message.warning("Veuillez vous connecter pour acheter ce cours");
      navigate("/login");
      return;
    }
    setConfirmModalVisible(true);
  };

  const handleConfirmPurchase = () => {
    setConfirmModalVisible(false);
    setPaymentModalVisible(true);
  };

  const handlePaymentSuccess = (paymentResult) => {
    setPaymentModalVisible(false);
    message.success(`Félicitations ! Vous avez acheté "${course.title}" avec succès !`);
    setTimeout(() => navigate('/my-purchases'), 2500);
  };

  const handlePaymentError = (error) => {
    console.error('Erreur de paiement:', error);
    message.error("Erreur lors du paiement. Veuillez réessayer.");
  };

  const handlePaymentCancel = () => {
    setPaymentModalVisible(false);
  };

  const handleFreeCourse = () => {
    const userId = getUserId();
    if (!userId) {
      message.warning("Veuillez vous connecter pour vous inscrire");
      navigate("/login");
      return;
    }
    handleBuyCourse();
  };

  /* ---- Loading / Error ---- */
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f7f8fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}>
        <style>{`
          @keyframes nomidya-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.7; }
          }
          @keyframes nomidya-rotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes nomidya-fade-in {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes nomidya-bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-12px); }
          }
          @keyframes nomidya-progress {
            0% { width: 0%; margin-left: 0%; }
            50% { width: 70%; margin-left: 15%; }
            100% { width: 0%; margin-left: 100%; }
          }
          .nomidya-loader-card {
            background: white;
            border-radius: 24px;
            padding: 60px 50px;
            box-shadow: 0 25px 50px rgba(0, 84, 166, 0.12);
            text-align: center;
            max-width: 500px;
            width: 100%;
            animation: nomidya-fade-in 0.6s ease-out;
            border: 1px solid rgba(0, 84, 166, 0.08);
          }
          .nomidya-loader-icon-wrapper {
            position: relative;
            width: 100px;
            height: 100px;
            margin: 0 auto 32px;
          }
          .nomidya-loader-ring {
            position: absolute;
            top: 0; left: 0;
            width: 100px; height: 100px;
            border-radius: 50%;
            border: 4px solid transparent;
            border-top-color: ${C.blue};
            border-right-color: ${C.orange};
            animation: nomidya-rotate 1.2s linear infinite;
          }
          .nomidya-loader-ring-2 {
            position: absolute;
            top: 10px; left: 10px;
            width: 80px; height: 80px;
            border-radius: 50%;
            border: 3px solid transparent;
            border-bottom-color: ${C.blueDark};
            border-left-color: ${C.orangeLight};
            animation: nomidya-rotate 1.5s linear infinite reverse;
          }
          .nomidya-loader-center {
            position: absolute;
            top: 50%; left: 50%;
            transform: translate(-50%, -50%);
            width: 50px; height: 50px;
            background: linear-gradient(135deg, ${C.blue} 0%, ${C.orange} 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 24px;
            animation: nomidya-pulse 2s ease-in-out infinite;
            box-shadow: 0 8px 24px rgba(0, 84, 166, 0.3);
          }
          .nomidya-loader-title {
            font-size: 22px;
            font-weight: 700;
            color: ${C.blueDark};
            margin: 0 0 8px;
            letter-spacing: -0.3px;
          }
          .nomidya-loader-subtitle {
            font-size: 14px;
            color: ${C.textLight};
            margin: 0 0 24px;
            line-height: 1.6;
          }
          .nomidya-loader-dots {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 20px;
          }
          .nomidya-loader-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            animation: nomidya-bounce 1.4s ease-in-out infinite;
          }
          .nomidya-loader-dot:nth-child(1) { background: ${C.blue}; animation-delay: 0s; }
          .nomidya-loader-dot:nth-child(2) { background: ${C.orange}; animation-delay: 0.2s; }
          .nomidya-loader-dot:nth-child(3) { background: ${C.blueDark}; animation-delay: 0.4s; }
          .nomidya-loader-progress {
            width: 100%;
            height: 4px;
            background: #e8eef5;
            border-radius: 4px;
            overflow: hidden;
            margin-top: 24px;
          }
          .nomidya-loader-progress-bar {
            height: 100%;
            background: linear-gradient(90deg, ${C.blue} 0%, ${C.orange} 100%);
            border-radius: 4px;
            width: 30%;
            animation: nomidya-progress 2s ease-in-out infinite;
          }
        `}</style>

        <div className="nomidya-loader-card">
          <div className="nomidya-loader-icon-wrapper">
            <div className="nomidya-loader-ring"></div>
            <div className="nomidya-loader-ring-2"></div>
            <div className="nomidya-loader-center">
              <BookOutlined />
            </div>
          </div>

          <h2 className="nomidya-loader-title">
            Chargement du cours
          </h2>

          <p className="nomidya-loader-subtitle">
            Préparation des détails et contenu...
          </p>

          <div className="nomidya-loader-dots">
            <div className="nomidya-loader-dot"></div>
            <div className="nomidya-loader-dot"></div>
            <div className="nomidya-loader-dot"></div>
          </div>

          <div className="nomidya-loader-progress">
            <div className="nomidya-loader-progress-bar"></div>
          </div>
        </div>
      </div>
    );
  }
  if (!course) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <p style={{ fontSize: 16, color: C.textLight }}>Cours introuvable.</p>
        <Button onClick={() => navigate("/courses")} style={{ borderRadius: 10 }}>Retour aux cours</Button>
      </div>
    );
  }

  const price = course.latest_price || course.price || 0;
  const beforePrice = course.before_price || 0;
  const discount = beforePrice > price && beforePrice > 0 ? Math.round(((beforePrice - price) / beforePrice) * 100) : 0;
  const isFree = price === 0;

  const stats = [
    course.lessons > 0 && { icon: <BookOutlined />, label: "Leçons", value: course.lessons, color: C.blue },
    hasContent(course.duration) && { icon: <ClockCircleOutlined />, label: "Durée", value: course.duration, color: "#7c3aed" },
    hasContent(course.access_time) && { icon: <SafetyCertificateOutlined />, label: "Accès", value: course.access_time, color: C.green },
    course.category && { icon: <AppstoreOutlined />, label: "Catégorie", value: course.category.libelle || course.category.name, color: C.orange },
  ].filter(Boolean);

  const trustBadges = [
    { icon: <SafetyCertificateOutlined />, text: "Certificat inclus" },
    { icon: <GlobalOutlined />, text: "Accès illimité" },
    { icon: <CustomerServiceOutlined />, text: "Support dédié" },
    { icon: <LockOutlined />, text: "Paiement sécurisé" },
  ];

  return (
    <div className="cl-landing">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,500;0,9..40,700;0,9..40,800&family=Playfair+Display:wght@600;700;800&display=swap');

        .cl-landing {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          color: ${C.text};
          background: ${C.bg};
          overflow-x: hidden;
        }

        /* ===== BACK BTN ===== */
        .cl-back {
          max-width: 1200px;
          margin: 0 auto;
          padding: 16px 24px 0;
        }

        /* ===== HERO ===== */
        .cl-hero {
          position: relative;
          background: linear-gradient(135deg, ${C.blueDeep} 0%, ${C.blueDark} 40%, ${C.blue} 100%);
          padding: 50px 0 80px;
          overflow: hidden;
        }
        .cl-hero::before {
          content: '';
          position: absolute;
          top: -100px; right: -100px;
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(232,134,12,0.15) 0%, transparent 70%);
          border-radius: 50%;
        }
        .cl-hero::after {
          content: '';
          position: absolute;
          bottom: -150px; left: -50px;
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(0,84,166,0.3) 0%, transparent 70%);
          border-radius: 50%;
        }
        .cl-hero-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 50px;
          align-items: center;
          position: relative;
          z-index: 2;
        }
        .cl-hero-image-wrap {
          position: relative;
        }
        .cl-hero-image {
          width: 100%;
          border-radius: 20px;
          box-shadow: 0 30px 60px rgba(0,0,0,0.4);
          aspect-ratio: 16/10;
          object-fit: cover;
          display: block;
        }
        .cl-price-badge {
          position: absolute;
          top: -14px; right: -14px;
          background: linear-gradient(135deg, ${C.orange}, ${C.orangeLight});
          color: white;
          padding: 14px 22px;
          border-radius: 16px;
          font-weight: 800;
          font-size: 22px;
          box-shadow: 0 8px 24px rgba(232,134,12,0.45);
          z-index: 3;
          line-height: 1;
        }
        .cl-price-badge small {
          display: block;
          font-size: 11px;
          font-weight: 500;
          opacity: 0.9;
          margin-top: 2px;
        }
        .cl-discount-badge {
          position: absolute;
          top: 16px; left: 16px;
          background: #dc2626;
          color: white;
          padding: 6px 14px;
          border-radius: 20px;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 12px rgba(220,38,38,0.4);
          animation: cl-pulse 2s ease-in-out infinite;
        }
        @keyframes cl-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .cl-hero-info { color: white; }
        .cl-hero-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
        }
        .cl-hero-tag {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          color: white;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          backdrop-filter: blur(8px);
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .cl-hero-tag.approved {
          background: rgba(0,135,90,0.25);
          border-color: rgba(0,135,90,0.4);
        }
        .cl-hero-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 38px;
          font-weight: 800;
          line-height: 1.2;
          color: white;
          margin: 0 0 16px;
          letter-spacing: -0.5px;
        }
        .cl-hero-desc {
          font-size: 16px;
          line-height: 1.7;
          color: rgba(255,255,255,0.8);
          margin-bottom: 24px;
          max-width: 520px;
        }
        .cl-hero-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
        }
        .cl-hero-meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.85);
          font-size: 14px;
          font-weight: 500;
        }
        .cl-hero-meta-item .anticon { font-size: 16px; color: ${C.orange}; }
        .cl-hero-actions {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        /* ===== BUTTONS ===== */
        .cl-btn-buy {
          background: linear-gradient(135deg, ${C.orange}, ${C.orangeLight}) !important;
          border: none !important;
          border-radius: 14px !important;
          height: 54px !important;
          padding: 0 36px !important;
          font-size: 16px !important;
          font-weight: 700 !important;
          color: white !important;
          box-shadow: 0 8px 24px rgba(232,134,12,0.4) !important;
          display: inline-flex !important;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease !important;
        }
        .cl-btn-buy:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 32px rgba(232,134,12,0.5) !important;
        }
        .cl-btn-free {
          background: linear-gradient(135deg, ${C.green}, #059669) !important;
          border: none !important;
          border-radius: 14px !important;
          height: 54px !important;
          padding: 0 36px !important;
          font-size: 16px !important;
          font-weight: 700 !important;
          color: white !important;
          box-shadow: 0 8px 24px rgba(0,135,90,0.4) !important;
          display: inline-flex !important;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease !important;
        }
        .cl-btn-free:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 32px rgba(0,135,90,0.5) !important;
        }
        .cl-btn-icon {
          width: 48px !important; height: 48px !important;
          border-radius: 14px !important;
          background: rgba(255,255,255,0.12) !important;
          border: 1px solid rgba(255,255,255,0.2) !important;
          color: white !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease !important;
        }
        .cl-btn-icon:hover { background: rgba(255,255,255,0.25) !important; transform: scale(1.05); }
        .cl-btn-icon.liked {
          background: rgba(220,38,38,0.2) !important;
          border-color: rgba(220,38,38,0.4) !important;
          color: #f87171 !important;
        }

        /* ===== WAVE ===== */
        .cl-wave { margin-top: -40px; position: relative; z-index: 1; }

        /* ===== STATS ===== */
        .cl-stats {
          max-width: 1200px;
          margin: -30px auto 40px;
          padding: 0 24px;
          position: relative;
          z-index: 10;
        }
        .cl-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 16px;
        }
        .cl-stat-card {
          background: white;
          border-radius: 16px;
          padding: 24px 16px;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          border: 1px solid ${C.border};
          transition: all 0.3s ease;
        }
        .cl-stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.1); }
        .cl-stat-icon {
          width: 48px; height: 48px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          margin-bottom: 12px;
        }
        .cl-stat-value { font-size: 22px; font-weight: 800; color: ${C.text}; margin-bottom: 2px; }
        .cl-stat-label { font-size: 13px; color: ${C.textLight}; font-weight: 500; }

        /* ===== TRUST ===== */
        .cl-trust {
          max-width: 1200px;
          margin: 0 auto 40px;
          padding: 0 24px;
        }
        .cl-trust-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .cl-trust-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 20px;
          background: white;
          border-radius: 14px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.04);
          border: 1px solid ${C.border};
          transition: all 0.2s;
        }
        .cl-trust-item:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
        .cl-trust-icon {
          width: 42px; height: 42px;
          border-radius: 12px;
          background: ${C.blue}10;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: ${C.blue};
          flex-shrink: 0;
        }
        .cl-trust-text { font-size: 13px; font-weight: 600; color: ${C.text}; }

        /* ===== CONTENT ===== */
        .cl-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }
        .cl-grid {
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 32px;
          align-items: start;
        }

        /* ===== SECTIONS ===== */
        .cl-section {
          background: white;
          border-radius: 20px;
          padding: 32px;
          margin-bottom: 24px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
          border: 1px solid ${C.border};
        }
        .cl-section-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 24px;
        }
        .cl-section-icon {
          width: 48px; height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: white;
          flex-shrink: 0;
        }
        .cl-section-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 22px;
          font-weight: 700;
          color: ${C.text};
          margin: 0;
        }
        .cl-section-body {
          font-size: 15px;
          line-height: 1.8;
          color: #4a5568;
        }

        /* ===== LEARN ITEMS ===== */
        .cl-learn-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 12px;
        }
        .cl-learn-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
          border-radius: 12px;
          border: 1px solid #bbf7d0;
          transition: all 0.2s;
        }
        .cl-learn-item:hover { transform: translateX(4px); box-shadow: 0 4px 12px rgba(0,135,90,0.1); }
        .cl-learn-item .anticon { color: ${C.green}; font-size: 18px; margin-top: 2px; flex-shrink: 0; }
        .cl-learn-item span { color: #2d3748; font-size: 14px; line-height: 1.5; }

        .cl-req-item {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 12px 16px; background: #fffbeb;
          border-radius: 12px; border: 1px solid #fde68a; margin-bottom: 10px;
        }
        .cl-req-item .anticon { color: #f59e0b; font-size: 16px; margin-top: 2px; }

        .cl-audience-item {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 12px 16px; background: #fdf2f8;
          border-radius: 12px; border: 1px solid #fbcfe8; margin-bottom: 10px;
        }
        .cl-audience-item .anticon { color: #ec4899; font-size: 16px; margin-top: 2px; }

        /* ===== SIDEBAR ===== */
        .cl-sidebar { position: sticky; top: 80px; }
        .cl-sidebar-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          border: 1px solid ${C.border};
          margin-bottom: 20px;
        }
        .cl-sidebar-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 18px; font-weight: 700; color: ${C.text}; margin: 0 0 18px;
        }
        .cl-sidebar-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 12px 0; border-bottom: 1px solid #f1f5f9;
        }
        .cl-sidebar-row:last-child { border-bottom: none; }
        .cl-sidebar-label { color: ${C.textLight}; font-size: 14px; }
        .cl-sidebar-value { color: ${C.text}; font-weight: 700; font-size: 14px; }

        /* ===== CTA CARD ===== */
        .cl-cta-card {
          background: linear-gradient(135deg, ${C.blueDeep} 0%, ${C.blue} 100%);
          border-radius: 20px;
          padding: 32px;
          text-align: center;
          box-shadow: 0 12px 40px rgba(0,84,166,0.3);
          margin-bottom: 20px;
          position: relative;
          overflow: hidden;
        }
        .cl-cta-card::before {
          content: '';
          position: absolute;
          top: -40px; right: -40px;
          width: 120px; height: 120px;
          background: rgba(232,134,12,0.15);
          border-radius: 50%;
        }
        .cl-cta-title {
          font-family: 'Playfair Display', Georgia, serif;
          color: white; font-size: 22px; font-weight: 700; margin: 16px 0 10px;
        }
        .cl-cta-text {
          color: rgba(255,255,255,0.75); font-size: 14px; margin-bottom: 20px;
        }
        .cl-cta-price {
          font-size: 36px; font-weight: 800; color: white; margin-bottom: 4px;
        }
        .cl-cta-old-price {
          text-decoration: line-through; color: rgba(255,255,255,0.5);
          font-size: 16px; margin-bottom: 6px;
        }
        .cl-cta-discount {
          display: inline-block;
          background: #dc2626;
          color: white;
          padding: 4px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 20px;
        }
        .cl-cta-btn-buy {
          width: 100%;
          background: linear-gradient(135deg, ${C.orange}, ${C.orangeLight}) !important;
          border: none !important;
          border-radius: 14px !important;
          height: 52px !important;
          font-size: 16px !important;
          font-weight: 700 !important;
          color: white !important;
          box-shadow: 0 6px 20px rgba(232,134,12,0.4) !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s !important;
        }
        .cl-cta-btn-buy:hover { transform: translateY(-2px) !important; box-shadow: 0 10px 28px rgba(232,134,12,0.5) !important; }
        .cl-cta-btn-free {
          width: 100%;
          background: linear-gradient(135deg, ${C.green}, #059669) !important;
          border: none !important;
          border-radius: 14px !important;
          height: 52px !important;
          font-size: 16px !important;
          font-weight: 700 !important;
          color: white !important;
          box-shadow: 0 6px 20px rgba(0,135,90,0.4) !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s !important;
        }
        .cl-cta-btn-free:hover { transform: translateY(-2px) !important; }
        .cl-cta-guarantee {
          display: flex; align-items: center; justify-content: center;
          gap: 6px; margin-top: 14px; color: rgba(255,255,255,0.6); font-size: 12px;
        }

        /* ===== BOTTOM BANNER ===== */
        .cl-banner {
          background: linear-gradient(135deg, ${C.orange}12, ${C.orange}06);
          border: 2px solid ${C.orange}30;
          border-radius: 20px;
          padding: 36px 40px;
          max-width: 1200px;
          margin: 0 auto 50px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }
        .cl-banner-text h3 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 24px; font-weight: 700; color: ${C.text}; margin: 0 0 6px;
        }
        .cl-banner-text p { color: ${C.textLight}; font-size: 14px; margin: 0; }

        /* ===== CONFIRM MODAL ===== */
        .cl-confirm-card {
          display: flex; gap: 14px; padding: 16px;
          background: ${C.bg}; border-radius: 12px;
          border: 1px solid ${C.border}; margin-bottom: 16px;
        }
        .cl-confirm-img {
          width: 80px; height: 80px;
          object-fit: cover; border-radius: 12px;
        }
        .cl-confirm-title {
          margin: 0 0 4px; color: ${C.blueDark};
          font-size: 16px; font-weight: 700;
        }
        .cl-confirm-meta { color: ${C.textLight}; font-size: 12px; margin: 0 0 6px; }
        .cl-confirm-price { font-size: 20px; font-weight: 800; color: ${C.blue}; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 1024px) {
          .cl-grid { grid-template-columns: 1fr; }
          .cl-sidebar { position: static; }
        }
        @media (max-width: 768px) {
          .cl-hero { padding: 32px 0 60px; }
          .cl-hero-inner { grid-template-columns: 1fr; gap: 28px; padding: 0 16px; }
          .cl-hero-title { font-size: 26px; }
          .cl-hero-desc { font-size: 14px; }
          .cl-hero-image { border-radius: 14px; }
          .cl-price-badge { top: -10px; right: -6px; font-size: 18px; padding: 10px 16px; border-radius: 12px; }
          .cl-hero-meta { gap: 12px; }
          .cl-hero-meta-item { font-size: 12px; }
          .cl-btn-buy, .cl-btn-free { height: 48px !important; padding: 0 24px !important; font-size: 14px !important; width: 100%; justify-content: center; }
          .cl-hero-actions { width: 100%; }
          .cl-stats { margin-top: -20px; }
          .cl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .cl-stat-card { padding: 16px 12px; }
          .cl-stat-icon { width: 40px; height: 40px; font-size: 18px; }
          .cl-stat-value { font-size: 18px; }
          .cl-content { padding: 0 16px 40px; }
          .cl-section { padding: 20px; border-radius: 14px; }
          .cl-section-title { font-size: 18px; }
          .cl-section-icon { width: 40px; height: 40px; font-size: 18px; }
          .cl-learn-grid { grid-template-columns: 1fr; }
          .cl-trust-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .cl-trust-item { padding: 14px; }
          .cl-trust-text { font-size: 11px; }
          .cl-banner {
            flex-direction: column; text-align: center;
            padding: 24px 20px; margin: 0 16px 40px;
          }
          .cl-banner-text h3 { font-size: 20px; }
          .cl-cta-card { padding: 24px; }
          .cl-cta-title { font-size: 20px; }
          .cl-cta-price { font-size: 28px; }
          .cl-sidebar-card { padding: 20px; }
        }
        @media (max-width: 480px) {
          .cl-hero { padding: 24px 0 50px; }
          .cl-hero-title { font-size: 22px; }
          .cl-hero-tags { gap: 6px; }
          .cl-hero-tag { font-size: 10px; padding: 4px 10px; }
          .cl-btn-icon { width: 42px !important; height: 42px !important; }
          .cl-stat-value { font-size: 16px; }
          .cl-stat-label { font-size: 11px; }
          .cl-trust-grid { grid-template-columns: 1fr 1fr; }
        }
      `}</style>

      {/* ===== BACK BUTTON ===== */}
      {/* <div className="cl-back">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}
          style={{ borderRadius: 10, fontWeight: 500 }}>
          Retour
        </Button>
      </div> */}

      {/* ===== HERO ===== */}
      {/* ===== HERO ===== */}
      <section className="cl-hero">
        {/* ⭐ Bouton Retour flottant dans le hero */}
        <button
          onClick={() => navigate(-1)}
          style={{
            position: 'absolute',
            top: 24,
            left: 24,
            zIndex: 10,
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: 30,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
            e.currentTarget.style.transform = 'translateX(-4px)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          <ArrowLeftOutlined /> Retour
        </button>

        <div className="cl-hero-inner">
          <div className="cl-hero-image-wrap">
            <img src={imageUrl || "/default-image.png"} alt={course.title}
              className="cl-hero-image"
              onError={(e) => { e.target.onerror = null; e.target.src = "/default-image.png"; }} />
            <div className="cl-price-badge">
              {isFree ? "GRATUIT" : `${price} USD`}
              {!isFree && <small>Accès complet</small>}
            </div>
            {discount > 0 && <div className="cl-discount-badge">-{discount}% OFF</div>}
          </div>

          <div className="cl-hero-info">
            <div className="cl-hero-tags">
              {course.category && (
                <span className="cl-hero-tag"><AppstoreOutlined /> {course.category.libelle || course.category.name}</span>
              )}
              {hasContent(course.access_time) && (
                <span className="cl-hero-tag"><ClockCircleOutlined /> {course.access_time}</span>
              )}
              {course.approved && (
                <span className="cl-hero-tag approved"><CheckCircleOutlined /> Approuvé</span>
              )}
            </div>

            <h1 className="cl-hero-title">{course.title}</h1>

            {hasContent(course.short_desc) && (
              <p className="cl-hero-desc">
                {stripHtml(course.short_desc).length > 180
                  ? stripHtml(course.short_desc).substring(0, 180) + "…"
                  : stripHtml(course.short_desc)}
              </p>
            )}

            <div className="cl-hero-meta">
              {course.lessons > 0 && <div className="cl-hero-meta-item"><BookOutlined /> {course.lessons} leçons</div>}
              {hasContent(course.duration) && <div className="cl-hero-meta-item"><ClockCircleOutlined /> {course.duration}</div>}
              <div className="cl-hero-meta-item"><SafetyCertificateOutlined /> Certificat inclus</div>
            </div>

            <div className="cl-hero-actions">
              {isFree ? (
                <Button type="primary" size="large" className="cl-btn-free" onClick={handleFreeCourse}>
                  <PlayCircleOutlined style={{ fontSize: 20 }} />
                  S'inscrire gratuitement
                  <ArrowRightOutlined style={{ fontSize: 14 }} />
                </Button>
              ) : (
                <Button type="primary" size="large" className="cl-btn-buy" onClick={handleBuyCourse}>
                  <ShoppingCartOutlined style={{ fontSize: 20 }} />
                  Acheter maintenant — {price} USD
                  <ArrowRightOutlined style={{ fontSize: 14 }} />
                </Button>
              )}
              <Button type="text" size="large" className={`cl-btn-icon ${liked ? 'liked' : ''}`}
                onClick={() => setLiked(!liked)} icon={<HeartOutlined style={{ fontSize: 20 }} />} />
              <Button type="text" size="large" className="cl-btn-icon"
                icon={<ShareAltOutlined style={{ fontSize: 20 }} />} />
            </div>
          </div>
        </div>
      </section>

      {/* ===== WAVE ===== */}
      <div className="cl-wave">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%' }}>
          <path d="M0 0L60 5C120 10 240 20 360 25C480 30 600 30 720 25C840 20 960 10 1080 8C1200 6 1320 12 1380 15L1440 18V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0V0Z" fill="${C.bg}" />
        </svg>
      </div>

      {/* ===== STATS ===== */}
      {stats.length > 0 && (
        <section className="cl-stats">
          <div className="cl-stats-grid">
            {stats.map((stat, i) => (
              <div key={i} className="cl-stat-card">
                <div className="cl-stat-icon" style={{ background: `${stat.color}12`, color: stat.color }}>{stat.icon}</div>
                <div className="cl-stat-value">{stat.value}</div>
                <div className="cl-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== TRUST ===== */}
      <section className="cl-trust">
        <div className="cl-trust-grid">
          {trustBadges.map((badge, i) => (
            <div key={i} className="cl-trust-item">
              <div className="cl-trust-icon">{badge.icon}</div>
              <span className="cl-trust-text">{badge.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== MAIN CONTENT ===== */}
      <section className="cl-content">
        <div className="cl-grid">
          {/* Left */}
          <div>
            {hasContent(course.short_desc) && (
              <div className="cl-section">
                <div className="cl-section-header">
                  <div className="cl-section-icon" style={{ background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})` }}><BookOutlined /></div>
                  <h2 className="cl-section-title">Description du cours</h2>
                </div>
                <div className="cl-section-body" dangerouslySetInnerHTML={{ __html: course.short_desc }} />
              </div>
            )}

            {hasContent(course.overview) && (
              <div className="cl-section">
                <div className="cl-section-header">
                  <div className="cl-section-icon" style={{ background: "linear-gradient(135deg, #6366f1, #4f46e5)" }}><FileTextOutlined /></div>
                  <h2 className="cl-section-title">Aperçu du cours</h2>
                </div>
                <div className="cl-section-body" dangerouslySetInnerHTML={{ __html: course.overview }} />
              </div>
            )}

            {hasContent(course.what_you_will_learn) && (
              <div className="cl-section">
                <div className="cl-section-header">
                  <div className="cl-section-icon" style={{ background: `linear-gradient(135deg, ${C.green}, #059669)` }}><TrophyOutlined /></div>
                  <h2 className="cl-section-title">Ce que vous apprendrez</h2>
                </div>
                {(() => {
                  const items = splitItems(course.what_you_will_learn);
                  if (items.length > 1) {
                    return (
                      <div className="cl-learn-grid">
                        {items.map((item, i) => (
                          <div key={i} className="cl-learn-item"><CheckCircleOutlined /> <span>{item}</span></div>
                        ))}
                      </div>
                    );
                  }
                  return <div className="cl-section-body" dangerouslySetInnerHTML={{ __html: course.what_you_will_learn }} />;
                })()}
              </div>
            )}

            {hasContent(course.requirements) && (
              <div className="cl-section">
                <div className="cl-section-header">
                  <div className="cl-section-icon" style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}><ThunderboltOutlined /></div>
                  <h2 className="cl-section-title">Prérequis</h2>
                </div>
                {(() => {
                  const items = splitItems(course.requirements);
                  if (items.length > 1) {
                    return items.map((item, i) => (
                      <div key={i} className="cl-req-item"><StarOutlined /> <span>{item}</span></div>
                    ));
                  }
                  return <div className="cl-section-body" dangerouslySetInnerHTML={{ __html: course.requirements }} />;
                })()}
              </div>
            )}

            {hasContent(course.who_is_this_course_for) && (
              <div className="cl-section">
                <div className="cl-section-header">
                  <div className="cl-section-icon" style={{ background: "linear-gradient(135deg, #ec4899, #db2777)" }}><TeamOutlined /></div>
                  <h2 className="cl-section-title">À qui s'adresse ce cours ?</h2>
                </div>
                {(() => {
                  const items = splitItems(course.who_is_this_course_for);
                  if (items.length > 1) {
                    return items.map((item, i) => (
                      <div key={i} className="cl-audience-item"><CheckCircleOutlined /> <span>{item}</span></div>
                    ));
                  }
                  return <div className="cl-section-body" dangerouslySetInnerHTML={{ __html: course.who_is_this_course_for }} />;
                })()}
              </div>
            )}
          </div>

          {/* Right - Sidebar */}
          <div className="cl-sidebar">
            {/* CTA Card */}
            <div className="cl-cta-card">
              <ThunderboltOutlined style={{ fontSize: 36, color: C.orange }} />
              <div className="cl-cta-title">
                {isFree ? "Cours gratuit !" : "Investissez dans votre avenir"}
              </div>
              <div className="cl-cta-text">Accès immédiat à tout le contenu</div>
              <div className="cl-cta-price">{isFree ? "GRATUIT" : `${price} USD`}</div>
              {beforePrice > price && beforePrice > 0 && (
                <>
                  <div className="cl-cta-old-price">{beforePrice} USD</div>
                  <div className="cl-cta-discount">Économisez {discount}%</div>
                </>
              )}
              {isFree ? (
                <Button type="primary" size="large" block className="cl-cta-btn-free" onClick={handleFreeCourse}>
                  <PlayCircleOutlined /> S'inscrire gratuitement
                </Button>
              ) : (
                <Button type="primary" size="large" block className="cl-cta-btn-buy" onClick={handleBuyCourse}>
                  <ShoppingCartOutlined /> Acheter maintenant
                </Button>
              )}
              <div className="cl-cta-guarantee">
                <LockOutlined /> Paiement 100% sécurisé
              </div>
            </div>

            {/* Summary */}
            <div className="cl-sidebar-card">
              <h3 className="cl-sidebar-title">Résumé</h3>
              {[
                course.lessons > 0 && { label: "Leçons", value: course.lessons },
                hasContent(course.duration) && { label: "Durée", value: course.duration },
                hasContent(course.access_time) && { label: "Accès", value: course.access_time },
                course.category && { label: "Catégorie", value: course.category.libelle || course.category.name },
              ].filter(Boolean).map((item, i) => (
                <div key={i} className="cl-sidebar-row">
                  <span className="cl-sidebar-label">{item.label}</span>
                  <span className="cl-sidebar-value">{item.value}</span>
                </div>
              ))}
            </div>

            {/* Includes */}
            <div className="cl-sidebar-card">
              <h3 className="cl-sidebar-title">Ce cours comprend</h3>
              {[
                { icon: <BookOutlined style={{ color: C.blue }} />, text: `${course.lessons || "Plusieurs"} leçons complètes` },
                { icon: <SafetyCertificateOutlined style={{ color: C.green }} />, text: "Certificat de réussite" },
                { icon: <GlobalOutlined style={{ color: "#7c3aed" }} />, text: "Accès sur tous les appareils" },
                { icon: <CustomerServiceOutlined style={{ color: C.orange }} />, text: "Support formateur" },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 0',
                  borderBottom: i < 3 ? `1px solid ${C.border}` : 'none'
                }}>
                  {item.icon}
                  <span style={{ fontSize: 13, color: C.text }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== BOTTOM BANNER ===== */}
      <div style={{ padding: '0 24px' }}>
        <div className="cl-banner">
          <div className="cl-banner-text">
            <h3>Ne manquez pas cette opportunité</h3>
            <p>Rejoignez des milliers d'apprenants et développez vos compétences dès aujourd'hui.</p>
          </div>
          {isFree ? (
            <Button type="primary" size="large" className="cl-btn-free" onClick={handleFreeCourse}>
              <PlayCircleOutlined /> S'inscrire <ArrowRightOutlined />
            </Button>
          ) : (
            <Button type="primary" size="large" className="cl-btn-buy" onClick={handleBuyCourse}>
              <ShoppingCartOutlined /> Acheter — {price} USD <ArrowRightOutlined />
            </Button>
          )}
        </div>
      </div>

      {/* ===== CONFIRM MODAL ===== */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ExclamationCircleOutlined style={{ color: C.orange }} />
          <span>Confirmer votre achat</span>
        </div>}
        open={confirmModalVisible}
        onOk={handleConfirmPurchase}
        onCancel={() => setConfirmModalVisible(false)}
        okText="Procéder au paiement"
        cancelText="Annuler"
        okButtonProps={{ style: { backgroundColor: C.blue, borderColor: C.blue, borderRadius: 10, fontWeight: 600 } }}
        cancelButtonProps={{ style: { borderRadius: 10 } }}
        width={480}
      >
        <div style={{ padding: '12px 0' }}>
          <div className="cl-confirm-card">
            <img src={imageUrl} alt={course.title} className="cl-confirm-img" />
            <div style={{ flex: 1 }}>
              <h3 className="cl-confirm-title">{course.title}</h3>
              <p className="cl-confirm-meta">{course.lessons || 'N/A'} leçons • {course.duration || 'N/A'}</p>
              <div className="cl-confirm-price">
                {isFree ? "GRATUIT" : `${price} USD`}
              </div>
              {beforePrice > price && beforePrice > 0 && (
                <span style={{ textDecoration: 'line-through', color: '#aaa', fontSize: 13, marginLeft: 8 }}>{beforePrice} USD</span>
              )}
            </div>
          </div>
          <p style={{ textAlign: 'center', fontSize: 13, color: C.textLight }}>
            {isFree
              ? "Cliquez pour confirmer votre inscription gratuite."
              : "Cliquez sur « Procéder au paiement » pour continuer."
            }
          </p>
        </div>
      </Modal>

      {/* ===== PAYMENT WRAPPER ===== */}
      <PaymentWrapper
        visible={paymentModalVisible}
        course={course}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        onCancel={handlePaymentCancel}
      />
    </div>
  );
};

export default CourseLandingPage;